import { TokenType, type Token, type SourceRange, type SourceLocation } from "../types/tokens.js";
import { ALLOWED_LINK_SCHEMES, parseUrlScheme } from "./link-url.js";
import { stitchKebabTail, type TokenCursor } from "./kebab-name.js";
import { readNodeIdPathTail } from "./node-path.js";
import type {
  KrsFile,
  KrsNode,
  KrsEdge,
  LinkEntry,
  DeployBlock,
  DeployNode,
  DeployNodeProperties,
  ImportDeclaration,
  NodeIdPath,
  Diagnostic,
  DiagnosticCode,
  DiagnosticParamsByCode,
  ParseResult,
  LogicalNodeKind,
  DeployNodeKind,
  DomainNode,
  ServiceNode,
  ClientNode,
  SystemNode,
  CommonProperties,
  OrganizationBlock,
  BoundaryBlock,
  FacetBlock,
  TeamNode,
  MemberNode,
  OrgNode,
  TableNode,
  QueueItemNode,
  BucketNode,
  DatabaseNode,
  QueueGroupNode,
  StorageNode,
  LegendBlock,
  LegendEntry,
  LegendRefTarget,
  LegendViewScope,
} from "../types/ast.js";
import { INFRA_KIND_SET, createEmptyKrsFile } from "../types/ast.js";
import { LOGICAL_CONTAINMENT } from "../builtins/reference-data.js";
import { Lexer } from "../lexer/lexer.js";
import { isRecognizedResourceOperation, type CrudVerb } from "../spec/operations.js";
import type { ResourceOperation } from "../spec/operations.js";
import {
  validateOwnsReferences,
  validateContainsReferences,
  validateScopedContainsReferences,
  validatePhysicalRefs,
  validateFacetDeclarations,
  buildFacetIndex,
  buildOwnerIndex,
  buildBoundaryMembership,
  buildScopedBoundaryMembership,
  migrationPriority,
} from "./reference-validation.js";

/**
 * Which kinds may host a scoped `boundary` block (#2036).
 *
 * The rule is "a boundary can be declared wherever a canvas is drawn" — a frame
 * encloses peers on one canvas, so placement and canvas correspond 1:1. Kinds
 * that draw a drill-down canvas (and infra blocks, whose leaf children are drawn
 * as top-level nodes there) can host one; leaves and `entity` (which takes no
 * node children) cannot.
 *
 * Keyed by every {@link LogicalNodeKind} rather than a set literal, so adding a
 * kind fails to typecheck until its placement is decided (TPL-1720).
 */
const BOUNDARY_HOST_KIND = {
  system: true,
  service: true,
  domain: true,
  usecase: true,
  database: true,
  queue: true,
  storage: true,
  entity: false,
  resource: false,
  user: false,
  client: false,
  table: false,
  "queue-item": false,
  bucket: false,
} as const satisfies Record<LogicalNodeKind, boolean>;

/** Intermediate shape returned by `parseOperationsList` before per-resource validation. */
interface ParsedOperation {
  verb: string;
  /**
   * Decorated CRUD candidates. `undefined` = no `:` decoration was present
   * (bare verb). `[]` = `:` was present but RHS was empty (will produce
   * `empty-crud-decoration`). Otherwise each entry's `recognized` flag tells
   * the resource builder whether to count it or emit `invalid-crud-decoration`.
   */
  decoration: { value: string; recognized: boolean }[] | undefined;
  /** Loc of the verb token (for diagnostic positioning). */
  decorationLoc: SourceLocation | undefined;
}

/**
 * The block keywords the parser accepts as logical node declarations.
 *
 * Exported so the hand-maintained Reference catalog can be fenced against the
 * parser instead of against a doc table generated from that same catalog —
 * see `builtins/reference-parser-sync.test.ts` (#2158, TPL-2158).
 */
export const LOGICAL_KEYWORDS = new Set<string>([
  "system",
  "service",
  "domain",
  "usecase",
  "entity",
  "resource",
  "user",
  "client",
  "database",
  "queue",
  "storage",
]);

// Infra block kinds that can appear as system-level children

// Recognized parameter keys per builtin lifecycle annotation (#1568). A param
// on any other annotation, or with another key, is dropped with an
// `annotation-param-unsupported` warning. Custom annotations stay param-less.
const ANNOTATION_PARAM_KEYS: Record<string, ReadonlySet<string>> = {
  deprecated: new Set(["until"]),
  experimental: new Set(["until"]),
  migration_target: new Set(["from"]),
  draft: new Set(["confidence"]),
};

// Migration-coexistence priority (`migrationPriority`) lives in
// reference-validation.ts since #2548 moved buildOwnerIndex there; it is
// imported below because buildNodePathIndex resolves duplicates by the same
// rule.

/**
 * The block keywords the parser accepts as deploy-unit declarations.
 *
 * Exported for the same reason as `LOGICAL_KEYWORDS`: the docs fence guard
 * (`scripts/lint/krs-fences.ts`) recognizes a bare ``` fence as `.krs` by its
 * declaration lines, and a hand-copied subset there would silently stop
 * recognizing whichever kind was added last (TPL-1720).
 */
export const DEPLOY_KEYWORDS = new Set<string>([
  "war",
  "jar",
  "oci",
  "lambda",
  "function",
  "assets",
  "job",
  "artifact",
  "store",
]);

const DEPLOY_PROPERTY_KEYWORDS = new Set<string>([
  "runtime",
  "realizes",
  "schedule",
  "image",
  "type",
]);

export class Parser {
  private tokens: Token[];
  private pos = 0;
  private diagnostics: Diagnostic[] = [];

  /** Cursor view over the token stream for shared helpers (kebab-name.ts). */
  private readonly cursor: TokenCursor = {
    peek: () => this.peek(),
    peekAt: (offset) => this.peekAt(offset),
    advance: () => this.advance(),
  };

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  static parse(source: string): ParseResult<KrsFile> {
    const tokens = new Lexer(source).tokenize();
    const parser = new Parser(tokens);
    return parser.parseFile();
  }

  private peek(): Token {
    return (
      this.tokens[this.pos] ?? {
        type: TokenType.EOF,
        value: "",
        loc: { line: 0, column: 0, offset: 0 },
      }
    );
  }

  private peekAt(offset: number): Token {
    return (
      this.tokens[this.pos + offset] ?? {
        type: TokenType.EOF,
        value: "",
        loc: { line: 0, column: 0, offset: 0 },
      }
    );
  }

  private advance(): Token {
    const token = this.peek();
    this.pos++;
    return token;
  }

  private expect(type: TokenType): Token {
    const token = this.peek();
    if (token.type !== type) {
      this.error("token-type-mismatch", {
        expected: String(type),
        got: String(token.type),
        value: token.value,
      });
      return token;
    }
    return this.advance();
  }

  private match(type: TokenType): boolean {
    if (this.peek().type === type) {
      this.advance();
      return true;
    }
    return false;
  }

  private error<K extends DiagnosticCode>(code: K, params: DiagnosticParamsByCode[K]): void {
    this.diagnostics.push({
      severity: "error",
      code,
      params,
      loc: this.range(this.peek().loc),
    } as Diagnostic);
  }

  private range(start: Token["loc"], end?: Token["loc"]): SourceRange {
    return {
      start: { ...start },
      end: end ? { ...end } : { ...start },
    };
  }

  parseFile(): ParseResult<KrsFile> {
    const file: KrsFile = createEmptyKrsFile();

    while (this.peek().type !== TokenType.EOF) {
      const token = this.peek();

      switch (token.type) {
        case TokenType.AtImport:
          file.styleImports.push(this.parseStyleImport());
          break;
        case TokenType.Import:
          file.nodeImports.push(this.parseNodeImport());
          break;
        case TokenType.System:
          file.systems.push(this.parseSystemBlock());
          break;
        case TokenType.Service:
          file.services.push(this.parseNodeDecl() as ServiceNode);
          break;
        case TokenType.Client:
          file.clients.push(this.parseNodeDecl() as ClientNode);
          break;
        case TokenType.Domain:
          file.domains.push(this.parseNodeDecl() as DomainNode);
          break;
        case TokenType.Database:
          file.databases.push(this.parseInfraBlock(this.advance(), "database") as DatabaseNode);
          break;
        case TokenType.Queue:
          file.queues.push(this.parseInfraBlock(this.advance(), "queue") as QueueGroupNode);
          break;
        case TokenType.Storage:
          file.storages.push(this.parseInfraBlock(this.advance(), "storage") as StorageNode);
          break;
        case TokenType.Deploy:
          file.deploys.push(this.parseDeployBlock());
          break;
        case TokenType.Organization:
          file.organizations.push(this.parseOrganizationBlock());
          break;
        case TokenType.Boundary:
          file.boundaries.push(this.parseBoundaryBlock());
          break;
        case TokenType.Facet:
          file.facets.push(this.parseFacetBlock());
          break;
        case TokenType.Legend:
          file.legends.push(this.parseLegendBlock());
          break;
        case TokenType.User:
          // A `user` is only valid inside a `system`. Report it with a
          // dedicated code (not a generic unexpected-token) and consume the
          // declaration so parsing recovers cleanly.
          this.error("top-level-declaration", { construct: "user" });
          this.parseNodeDecl();
          break;
        case TokenType.Entity: {
          // An `entity` is only valid as a `domain` child. Consume the whole
          // declaration and emit the dedicated diagnostic (not the generic
          // unexpected-token cascade) so the canonical mistake reads clearly.
          const stray = this.parseNodeDecl();
          this.diagnostics.push({
            severity: "error",
            code: "entity-not-in-domain",
            params: { parentKind: "top level" },
            loc: stray.loc,
          });
          break;
        }
        default:
          // A top-level edge (`A -> B`) is likewise only valid inside a
          // `system`; surface the same dedicated diagnostic and consume it.
          if (this.isEdgeStart(token)) {
            this.error("top-level-declaration", { construct: "edge" });
            this.parseEdge();
            break;
          }
          this.error("unexpected-token-root", {
            tokenType: String(token.type),
            value: token.value,
          });
          this.advance();
      }
    }

    const ownerResult = buildOwnerIndex(file);
    file.ownerIndex = ownerResult.membership;
    this.diagnostics.push(...ownerResult.diagnostics);
    const topLevelMembership = buildBoundaryMembership(file);
    file.boundaryMembership = topLevelMembership.membership;
    this.diagnostics.push(...topLevelMembership.diagnostics);
    const scopedMembership = buildScopedBoundaryMembership([
      ...file.systems,
      ...file.services,
      ...file.clients,
      ...file.domains,
      ...file.databases,
      ...file.queues,
      ...file.storages,
    ]);
    // Same root set as the scoped-boundary walk: `facets` is accepted on every
    // node kind, so the index must reach top-level orphans (a system-less
    // `service` / `client` / `domain` / infra block) and their descendants, not
    // just what hangs off a `system` (TPL-1160 / TPL-1720).
    file.facetIndex = buildFacetIndex([
      ...file.systems,
      ...file.services,
      ...file.clients,
      ...file.domains,
      ...file.databases,
      ...file.queues,
      ...file.storages,
    ]);
    file.scopedBoundaryMembership = scopedMembership.membership;
    this.diagnostics.push(...scopedMembership.diagnostics);
    // Top-level (system-less) services get the same per-parent duplicate-child
    // check as services nested in a system, so e.g. a usecase and entity
    // sharing an id under a parked service's domain are caught.
    for (const service of file.services) {
      this.collectNodeIds(service.children, new Set<string>());
    }
    // Same check for top-level infra blocks. Nested ones are reached via
    // `system.children`, but a system-less `database` / `queue` / `storage`
    // otherwise never gets one — and its leaf children are exactly the members a
    // scoped `boundary` addresses (#2036), which relies on sibling ids being
    // error-unique.
    for (const infra of [...file.databases, ...file.queues, ...file.storages]) {
      this.collectNodeIds(infra.children, new Set<string>());
    }
    file.nodePathIndex = this.buildNodePathIndex(file.systems, file.domains, [
      ...file.databases,
      ...file.queues,
      ...file.storages,
      ...file.clients,
    ]);
    this.diagnostics.push(...validateOwnsReferences(file));
    if (file.boundaries.length > 0) {
      this.diagnostics.push(...validateContainsReferences(file));
    }
    // Scoped boundaries resolve against direct children, so they are checked
    // per scope rather than against the whole model (#2036).
    this.diagnostics.push(...validateScopedContainsReferences(file));
    // A `resource` / `table` naming an infra block or leaf nothing declares
    // (#2078). Suppressed and re-derived by the ImportResolver like the checks
    // above, since the block is canonically declared in an imported infra file.
    this.diagnostics.push(...validatePhysicalRefs(file));
    // Declaration uniqueness is a property of the *merged* facet namespace: two
    // files may each declare `facet pii` with different metadata, and only the
    // merge sees that. The ImportResolver suppresses this code per file and
    // re-derives it there; single-file parses (LSP, `Parser.parse`) get the same
    // verdict here (TPL-2032).
    this.diagnostics.push(...validateFacetDeclarations(file.facets));

    return { value: file, diagnostics: this.diagnostics };
  }

  private parseStyleImport(): string {
    this.advance(); // @import
    const path = this.expect(TokenType.StringLiteral);
    return path.value;
  }

  private parseNodeImport(): ImportDeclaration {
    const start = this.advance(); // import

    // Wildcard import: import "file.krs"
    if (this.peek().type === TokenType.StringLiteral) {
      const path = this.advance();
      return { ids: [], path: path.value, loc: this.range(start.loc, path.loc) };
    }

    if (this.peek().type !== TokenType.LeftBrace) {
      this.error("expected-brace-or-string", {
        got: String(this.peek().type),
        value: this.peek().value,
      });
      // LeftBrace がない場合は空の import 宣言を返す
      return { ids: [], path: "", loc: this.range(start.loc) };
    }
    this.advance(); // {

    const ids: NodeIdPath[] = [];
    while (this.peek().type !== TokenType.RightBrace && this.peek().type !== TokenType.EOF) {
      if (this.peek().type === TokenType.Identifier) {
        // Read one path: Identifier (Dot Identifier)*
        const tail = readNodeIdPathTail(this.advance(), this.cursor);
        if (tail.dangling) {
          // dot without trailing identifier — record error and stop reading
          // segments for this entry; skip the bad token to make progress.
          this.error("expected-identifier", {
            got: String(tail.dangling.type),
            value: tail.dangling.value,
          });
          this.advance();
        }
        ids.push(tail.segments);
        this.match(TokenType.Comma);
      } else {
        // 予期しないトークン: エラーを記録してスキップ
        this.error("expected-identifier", {
          got: String(this.peek().type),
          value: this.peek().value,
        });
        this.advance();
      }
    }
    this.expect(TokenType.RightBrace);
    this.expect(TokenType.From);
    const path = this.expect(TokenType.StringLiteral);

    return {
      ids,
      path: path.value,
      loc: this.range(start.loc, path.loc),
    };
  }

  private parseSystemBlock(): import("../types/ast.js").SystemNode {
    return this.parseNodeDecl() as import("../types/ast.js").SystemNode;
  }

  private parseBlockContentsWithProperties(
    children: KrsNode[],
    edges: KrsEdge[],
    kind: LogicalNodeKind,
    properties: CommonProperties & {
      role?: string;
      team?: string;
      label?: string;
      resources?: import("../types/ast.js").ClientResource[];
      capabilities?: import("../types/ast.js").ClientCapability[];
      handles?: string[];
      delivers?: string[];
      operations?: ParsedOperation[];
      tableRef?: { parent: string; child: string };
      facets?: string[];
    },
    parentId?: string,
    /** Collector for scoped `boundary` blocks; omitted by callers whose kind can never host one. */
    boundaries?: BoundaryBlock[],
  ): void {
    while (this.peek().type !== TokenType.RightBrace && this.peek().type !== TokenType.EOF) {
      const token = this.peek();

      // Property: label
      if (token.type === TokenType.Label) {
        this.advance();
        if (this.peek().type === TokenType.StringLiteral) {
          properties.label = this.advance().value;
        } else {
          this.error("expected-string-after", { property: "label" });
        }
        continue;
      }

      // Property: description
      if (token.type === TokenType.Description) {
        this.advance();
        properties.description = this.parseDescriptionValue();
        continue;
      }

      // Property: link
      if (token.type === TokenType.Link) {
        this.advance();
        properties.links.push(this.parseLink());
        continue;
      }

      // Property: facets — cross-cutting membership (#2065 Part B). Accepted on
      // every kind: which facets an element belongs to is imposed from outside
      // the architecture, so no kind is structurally excluded (no
      // `property-not-for-node-kind` branch here, unlike role / handles).
      if (token.type === TokenType.Facets) {
        this.advance();
        properties.facets ??= [];
        this.parseFacetsList(properties.facets);
        continue;
      }

      // Property: role (user only)
      if (token.type === TokenType.Role) {
        if (kind === "user") {
          this.advance();
          if (this.peek().type === TokenType.StringLiteral) {
            properties.role = this.advance().value;
          } else {
            this.error("expected-string-after", { property: "role" });
          }
        } else {
          this.error("property-not-for-node-kind", { property: "role", nodeKind: kind });
          this.advance();
        }
        continue;
      }

      // Property: handles (client and service) — domain ids exposed to callers
      if (token.type === TokenType.Handles) {
        if (kind === "client" || kind === "service") {
          this.advance();
          const ids = this.parseHandlesList();
          if (ids.length > 0) {
            if (!properties.handles) properties.handles = [];
            properties.handles.push(...ids);
          }
        } else {
          this.error("property-not-for-node-kind", { property: "handles", nodeKind: kind });
          this.advance();
        }
        continue;
      }

      // Property: operations (resource only) — CRUD verbs the enclosing usecase
      // performs on this resource. Comma-separated identifiers; multiple
      // `operations` lines accumulate. Optional `verb:c[,c]` decoration maps
      // a non-CRUD verb to its CRUD intent (`list:read`, `replace:create,delete`).
      if (token.type === TokenType.Operations) {
        if (kind === "resource") {
          this.advance();
          const ops = this.parseOperationsList();
          if (ops.length > 0) {
            if (!properties.operations) properties.operations = [];
            properties.operations.push(...ops);
          }
        } else {
          this.error("property-not-for-node-kind", { property: "operations", nodeKind: kind });
          this.advance();
        }
        continue;
      }

      // Property: table (entity only) — physical mapping `table <InfraId>.<subId>`
      // to an infra sub-resource. Dot notation is required; the bare-id form is
      // reserved. Unlike the infra-leaf `table` block (parsed inside
      // parseInfraBlock), here `table` is a property keyword, so it never
      // collides with infra-block parsing.
      if (token.type === TokenType.Table) {
        if (kind === "entity") {
          this.advance();
          // Require the full `<InfraId>.<subId>` form. Guard BOTH sides so a
          // malformed mapping (`table .orders`, `table OrderDB.`, `table orders`)
          // leaves tableRef undefined, emits exactly one diagnostic, and never
          // consumes a stray token (`.`, `}`) as an id.
          const isId = (t: TokenType) =>
            t === TokenType.Identifier || t === TokenType.StringLiteral;
          if (isId(this.peek().type)) {
            const tail = readNodeIdPathTail(this.advance(), this.cursor, {
              maxSegments: 2,
              acceptStringSegments: true,
            });
            if (tail.segments.length === 2) {
              properties.tableRef = { parent: tail.segments[0], child: tail.segments[1] };
            } else {
              // Missing dot, or missing sub-id after the dot — do NOT consume
              // the following token (`}`, etc.).
              this.error("expected-id-after", { property: "table" });
            }
          } else {
            this.error("expected-id-or-string", { context: "entity table infra id" });
            // Consume a stray `.<id>` so we recover in one diagnostic, not three.
            if (this.peek().type === TokenType.Dot) {
              this.advance();
              if (isId(this.peek().type)) this.advance();
            }
          }
        } else {
          this.error("unexpected-token-in-block", {
            blockKind: kind,
            tokenType: String(token.type),
            value: token.value,
          });
          this.advance();
        }
        continue;
      }

      // Property: team — removed. Was deprecated (ADR-14); ownership is
      // now declared with an `organization` block + `owns`. The `team` keyword
      // itself stays valid as the organization team-block keyword (parsed elsewhere).
      if (token.type === TokenType.Team) {
        this.diagnostics.push({
          severity: "error",
          code: "team-property-removed",
          params: {},
          loc: this.range(token.loc),
        });
        this.advance();
        // Consume the value string (if any) so the rest of the block still parses.
        if (this.peek().type === TokenType.StringLiteral) {
          this.advance();
        }
        continue;
      }

      // Property: delivers (service only). Comma-separated list of client ids.
      if (token.type === TokenType.Delivers) {
        if (kind === "service") {
          this.advance();
          if (!properties.delivers) properties.delivers = [];
          // Parse one or more identifiers separated by commas.
          while (true) {
            if (
              this.peek().type === TokenType.Identifier ||
              this.peek().type === TokenType.StringLiteral
            ) {
              properties.delivers.push(this.advance().value);
            } else {
              this.error("expected-id-after", { property: "delivers" });
              break;
            }
            if (this.peek().type === TokenType.Comma) {
              this.advance();
              continue;
            }
            break;
          }
        } else {
          this.error("property-not-for-node-kind", { property: "delivers", nodeKind: kind });
          this.advance();
        }
        continue;
      }

      // Implicit-source edge: -> or --> (source = parent node ID)
      if ((token.type === TokenType.Arrow || token.type === TokenType.DashedArrow) && parentId) {
        edges.push(this.parseEdge(parentId));
        continue;
      }

      // Check for edge: Identifier/StringLiteral -> or -->
      if (this.isEdgeStart(token)) {
        const edge = this.parseEdge();
        if (parentId && edge.from !== parentId) {
          this.diagnostics.push({
            severity: "error",
            code: "edge-source-mismatch",
            params: { from: edge.from, parentId },
            loc: edge.loc,
          });
        }
        edges.push(edge);
        continue;
      }

      // Client-only: `resource <storageKind> "<name>"` declares operation-tied storage.
      // Distinguished from a regular resource declaration by the trailing string literal.
      if (
        kind === "client" &&
        token.type === TokenType.Resource &&
        this.peekAt(1).type === TokenType.Identifier &&
        this.peekAt(2).type === TokenType.StringLiteral
      ) {
        properties.resources ??= [];
        properties.resources.push(this.parseClientResource());
        continue;
      }

      // Client-only: `capability <name>` or `capability <name> { label "..." description "..." }`.
      // Identifier set is open by design — see docs/design/client-capability-modeling.md.
      if (kind === "client" && token.type === TokenType.Capability) {
        properties.capabilities ??= [];
        properties.capabilities.push(this.parseClientCapability());
        continue;
      }

      // Check for logical node
      // Infra block kinds (database/queue/storage) are only valid as direct children of system.
      if (this.isLogicalKeyword(token)) {
        if (INFRA_KIND_SET.has(token.value) && kind !== "system") {
          this.error("infra-not-in-context", {
            infraKind: token.value,
            parentKind: kind,
          });
          this.advance();
          continue;
        }
        // `entity` is only valid as a `domain` child. Consume the full
        // declaration for clean error recovery, then drop it.
        if (token.value === "entity" && kind !== "domain") {
          const node = this.parseNodeDecl();
          this.diagnostics.push({
            severity: "error",
            code: "entity-not-in-domain",
            params: { parentKind: kind },
            loc: node.loc,
          });
          continue;
        }
        // An `entity` block accepts no node children — only relations, a
        // `table` mapping, and label / description (the "no attributes"
        // invariant, TPL-1882). Reject and drop any nested logical
        // node (usecase / resource / …) instead of silently attaching it.
        if (kind === "entity") {
          const node = this.parseNodeDecl();
          this.diagnostics.push({
            severity: "error",
            code: "unexpected-token-in-block",
            params: { blockKind: "entity", tokenType: String(token.type), value: token.value },
            loc: node.loc,
          });
          continue;
        }
        const child = this.parseNodeDecl();
        // Nesting placement (#2165). `canContain` is the single definition of
        // which children a kind may hold; anything outside it carries no
        // defined semantics (`docs/concepts.ja.md` fixes the hierarchy as
        // service → domain → usecase → resource). This is a **warning**, not an
        // error: `.krs language v1.0` is frozen (ADR-1314), so the node is kept and
        // still renders. Error-ification is registered to Syntax 2.0 (#2162).
        if (!LOGICAL_CONTAINMENT.get(kind)?.has(child.kind)) {
          this.diagnostics.push({
            severity: "warning",
            code: "node-not-in-context",
            params: { childKind: child.kind, parentKind: kind },
            loc: child.loc,
          });
        }
        children.push(child);
        continue;
      }

      if (token.type === TokenType.Legend) {
        this.handleNestedLegend(kind);
        continue;
      }

      if (token.type === TokenType.Facet) {
        this.rejectNestedFacetBlock(kind);
        continue;
      }

      // A `boundary` block declared inside a node block (#2036). Parse it first
      // either way, so a misplaced one is consumed whole and recovery is clean
      // (same shape as the `entity-not-in-domain` branch above).
      if (token.type === TokenType.Boundary) {
        const block = this.parseBoundaryBlock();
        if (BOUNDARY_HOST_KIND[kind] && boundaries) {
          boundaries.push(block);
        } else {
          this.diagnostics.push({
            severity: "error",
            code: "boundary-not-in-context",
            params: { parentKind: kind },
            loc: block.loc,
          });
        }
        continue;
      }

      this.error("unexpected-token-in-block", {
        blockKind: "",
        tokenType: String(token.type),
        value: token.value,
      });
      this.advance();
    }
  }

  private parseDescriptionValue(): string {
    if (this.peek().type === TokenType.TripleQuote) {
      return this.advance().value;
    }
    if (this.peek().type === TokenType.StringLiteral) {
      return this.advance().value;
    }
    this.error("expected-string-after", { property: "description" });
    return "";
  }

  private parseClientResource(): import("../types/ast.js").ClientResource {
    const start = this.expect(TokenType.Resource);
    const kindToken = this.expect(TokenType.Identifier);
    const nameToken = this.expect(TokenType.StringLiteral);
    const kindName = kindToken.value;
    const isAllowed = (
      ["localStorage", "sessionStorage", "indexedDB", "opfs", "file", "keychain"] as const
    ).some((k) => k === kindName);
    if (!isAllowed) {
      this.diagnostics.push({
        severity: "error",
        code: "client-resource-invalid-kind",
        params: { kind: kindName, name: nameToken.value },
        loc: this.range(start.loc, nameToken.loc),
      });
    }
    return {
      storageKind: kindName as import("../types/ast.js").ClientResourceKind,
      name: nameToken.value,
      loc: this.range(start.loc, nameToken.loc),
    };
  }

  private parseClientCapability(): import("../types/ast.js").ClientCapability {
    const start = this.expect(TokenType.Capability);
    const nameToken = this.expect(TokenType.Identifier);
    // Capability identifiers are kebab-case by convention (e.g. screen-wake-lock,
    // face-id). Stitch the `<word> - <word>` token run back into one name —
    // shared with tags / annotations / legend refs (#2509, kebab-name.ts).
    const stitched = stitchKebabTail(nameToken, this.cursor);
    const name = stitched.name;
    let end: Token = stitched.end;
    let label: string | undefined;
    let description: string | undefined;

    if (this.peek().type === TokenType.LeftBrace) {
      this.advance();
      while (this.peek().type !== TokenType.RightBrace && this.peek().type !== TokenType.EOF) {
        const inner = this.peek();
        if (inner.type === TokenType.Label) {
          this.advance();
          if (this.peek().type === TokenType.StringLiteral) {
            label = this.advance().value;
          } else {
            this.error("expected-string-after", { property: "label" });
          }
          continue;
        }
        if (inner.type === TokenType.Description) {
          this.advance();
          description = this.parseDescriptionValue();
          continue;
        }
        this.error("unexpected-token-in-block", {
          blockKind: "capability",
          tokenType: String(inner.type),
          value: inner.value,
        });
        this.advance();
      }
      end = this.expect(TokenType.RightBrace);
    }

    return {
      name,
      ...(label !== undefined ? { label } : {}),
      ...(description !== undefined ? { description } : {}),
      loc: this.range(start.loc, end.loc),
    };
  }

  private parseLink(): LinkEntry {
    const start = this.peek().loc;
    const url = this.expect(TokenType.StringLiteral);
    let label: string | undefined;
    let end = url.loc;
    if (this.peek().type === TokenType.StringLiteral) {
      const labelToken = this.advance();
      label = labelToken.value;
      end = labelToken.loc;
    }
    const loc = this.range(start, end);
    // Trust-boundary scheme allowlist (#1525 / TPL-168): link URLs are
    // untrusted .krs content rendered as <a href> in the app and the VS Code
    // webview, where a javascript: URL executes in the app origin. We WARN here
    // (validate once at the boundary) but keep the link in the AST so Format /
    // round-trip never silently deletes the user's source. The href-rendering
    // surfaces filter with isSafeLinkUrl before creating an anchor. The WHATWG
    // URL parser matches browser href semantics (case folding, tab/newline
    // stripping), so tricks like "JaVaScRiPt:" normalize before the check;
    // anything unparseable (incl. relative paths) is reported too.
    const scheme = parseUrlScheme(url.value);
    if (scheme === null || !ALLOWED_LINK_SCHEMES.has(scheme)) {
      this.diagnostics.push({
        severity: "warning",
        code: "link-url-scheme-not-allowed",
        params: { url: url.value, scheme: scheme ?? "" },
        loc,
      });
    }
    return { url: url.value, label, loc };
  }

  /**
   * Parse a comma-separated list of identifiers/strings following `handles`.
   * Accepts both `handles A` and `handles A, B, C` forms.
   * Stops at the first non-identifier / non-string token (e.g. newline-introduced
   * keyword on the next line — the lexer is whitespace-insensitive so the
   * comma is the delimiter).
   */
  /**
   * Parse the right-hand side of an `operations` property. Each entry is
   * either a bare verb (`create`, `list`) or a decorated verb (`list:read`,
   * `replace:create,delete`). Comma-separated; the comma after a decoration
   * RHS is recognised as a verb separator only when the next identifier is
   * followed by `:` — otherwise the bare identifier becomes a CRUD-RHS
   * continuation that the per-verb validator will accept (recognised CRUD)
   * or flag with `invalid-crud-decoration` (anything else).
   *
   * Diagnostics are *deferred* to the resource builder so the resourceId is
   * available; we return raw entries plus malformed-flag info here.
   */
  private parseOperationsList(): ParsedOperation[] {
    const out: ParsedOperation[] = [];
    if (!this.peekIsIdOrString()) {
      this.error("expected-id-after", { property: "operations" });
      return out;
    }
    out.push(this.parseOneOperation());
    while (this.peek().type === TokenType.Comma) {
      this.advance();
      if (!this.peekIsIdOrString()) {
        this.error("expected-id-after", { property: "operations" });
        break;
      }
      out.push(this.parseOneOperation());
    }
    return out;
  }

  private parseOneOperation(): ParsedOperation {
    const verbToken = this.advance();
    const verb = verbToken.value;
    if (this.peek().type !== TokenType.Colon) {
      return { verb, decoration: undefined, decorationLoc: undefined };
    }
    this.advance(); // consume `:`
    const decoration: { value: string; recognized: boolean }[] = [];
    if (!this.peekIsIdOrString()) {
      // Empty RHS — emit empty-crud-decoration later in the resource builder.
      return { verb, decoration: [], decorationLoc: verbToken.loc };
    }
    decoration.push(this.consumeDecorationItem());
    while (this.peek().type === TokenType.Comma) {
      // Lookahead: comma + id + colon → this is a new verb, not RHS continuation.
      const next = this.tokens[this.pos + 1];
      const after = this.tokens[this.pos + 2];
      if (
        next &&
        (next.type === TokenType.Identifier || next.type === TokenType.StringLiteral) &&
        after &&
        after.type === TokenType.Colon
      ) {
        break;
      }
      this.advance(); // consume `,`
      if (!this.peekIsIdOrString()) {
        this.error("expected-id-after", { property: "operations" });
        break;
      }
      decoration.push(this.consumeDecorationItem());
    }
    return { verb, decoration, decorationLoc: verbToken.loc };
  }

  private consumeDecorationItem(): { value: string; recognized: boolean } {
    const tok = this.advance();
    return { value: tok.value, recognized: isRecognizedResourceOperation(tok.value) };
  }

  private peekIsIdOrString(): boolean {
    const t = this.peek().type;
    return t === TokenType.Identifier || t === TokenType.StringLiteral;
  }

  private parseHandlesList(): string[] {
    const ids: string[] = [];
    if (this.peek().type !== TokenType.Identifier && this.peek().type !== TokenType.StringLiteral) {
      this.error("expected-id-after", { property: "handles" });
      return ids;
    }
    ids.push(this.advance().value);
    while (this.peek().type === TokenType.Comma) {
      this.advance();
      if (
        this.peek().type !== TokenType.Identifier &&
        this.peek().type !== TokenType.StringLiteral
      ) {
        this.error("expected-id-after", { property: "handles" });
        break;
      }
      ids.push(this.advance().value);
    }
    return ids;
  }

  private isLogicalKeyword(token: Token): boolean {
    return LOGICAL_KEYWORDS.has(token.value) && this.isNodeKeywordType(token.type);
  }

  /**
   * True when `token` (the current token) begins an explicit edge: an
   * identifier or string literal followed by `->` (sync) or `-->` (async).
   */
  private isEdgeStart(token: Token): boolean {
    return (
      (token.type === TokenType.Identifier || token.type === TokenType.StringLiteral) &&
      (this.peekAt(1).type === TokenType.Arrow || this.peekAt(1).type === TokenType.DashedArrow)
    );
  }

  private isNodeKeywordType(type: TokenType): boolean {
    return (
      type === TokenType.System ||
      type === TokenType.Service ||
      type === TokenType.Domain ||
      type === TokenType.Usecase ||
      type === TokenType.Entity ||
      type === TokenType.Resource ||
      type === TokenType.User ||
      type === TokenType.Client ||
      type === TokenType.Database ||
      type === TokenType.Queue ||
      type === TokenType.Storage
    );
  }

  /** Accept either an identifier or a string literal as an id/reference token. */
  private parseIdOrString(context: string): Token {
    if (this.peek().type === TokenType.Identifier || this.peek().type === TokenType.StringLiteral) {
      return this.advance();
    }
    this.error("expected-id-or-string", { context });
    return this.peek();
  }

  private parseNodeDecl(): KrsNode {
    const start = this.advance(); // keyword
    const kind = start.value as LogicalNodeKind;

    // Delegate infra block parsing to dedicated methods
    if (INFRA_KIND_SET.has(kind)) {
      return this.parseInfraBlock(start, kind as "database" | "queue" | "storage");
    }

    // Special handling for resource: support dot-notation reference (resource OrderDB.C)
    if (kind === "resource") {
      return this.parseResourceDecl(start);
    }

    // id: accept identifier (e.g. ECommerce) or string literal (e.g. "e-commerce")
    let id: string;
    let idToken: Token;
    if (this.peek().type !== TokenType.Identifier && this.peek().type !== TokenType.StringLiteral) {
      this.error("expected-node-id", { kind });
      id = "__missing_id";
      idToken = start; // fallback location
    } else {
      idToken = this.advance();
      id = idToken.value;
    }

    // Optional tags
    const tags = this.parseTags();

    // Optional annotations
    const { names: annotations, params: annotationParams } = this.parseAnnotations();

    // Properties (label is now a property inside the block)
    const properties: CommonProperties & {
      role?: string;
      team?: string;
      label?: string;
      resources?: import("../types/ast.js").ClientResource[];
      capabilities?: import("../types/ast.js").ClientCapability[];
      handles?: string[];
      delivers?: string[];
      tableRef?: { parent: string; child: string };
      facets?: string[];
    } = {
      links: [],
    };

    // Optional body
    const children: KrsNode[] = [];
    const edges: KrsEdge[] = [];
    const boundaries: BoundaryBlock[] = [];
    let end = idToken;

    if (this.peek().type === TokenType.LeftBrace) {
      this.advance();
      // service / domain / entity own their edges' origin scope: an explicit
      // edge inside the block must start at the block id (edge-source-mismatch
      // otherwise). For entity this enforces the relation direction rule —
      // origin = the reference-holding entity.
      const parentId =
        kind === "service" || kind === "domain" || kind === "entity" ? id : undefined;
      this.parseBlockContentsWithProperties(
        children,
        edges,
        kind,
        properties,
        parentId,
        boundaries,
      );
      end = this.expect(TokenType.RightBrace);
    }

    const base = {
      id,
      label: properties.label,
      tags,
      annotations,
      ...(Object.keys(annotationParams).length > 0 ? { annotationParams } : {}),
      children,
      edges,
      // Omitted entirely when none were declared, so nodes in existing models
      // keep their exact shape (snapshot/round-trip stability).
      ...(boundaries.length > 0 ? { boundaries } : {}),
      ...(properties.facets && properties.facets.length > 0 ? { facets: properties.facets } : {}),
      loc: this.range(start.loc, end.loc),
    };

    switch (kind) {
      case "system":
        return {
          ...base,
          kind,
          properties: {
            description: properties.description,
            links: properties.links,
          },
        };
      case "service":
        return {
          ...base,
          kind,
          properties: {
            description: properties.description,
            links: properties.links,
            ...(properties.handles ? { handles: properties.handles } : {}),
            ...(properties.delivers && properties.delivers.length > 0
              ? { delivers: properties.delivers }
              : {}),
          },
        };
      case "user":
        return {
          ...base,
          kind,
          properties: {
            description: properties.description,
            links: properties.links,
            role: properties.role,
          },
        };
      case "client":
        return {
          ...base,
          kind,
          properties: {
            description: properties.description,
            links: properties.links,
            resources: properties.resources ?? [],
            capabilities: properties.capabilities ?? [],
            ...(properties.handles ? { handles: properties.handles } : {}),
          },
        };
      case "domain":
        return {
          ...base,
          kind,
          properties: {
            description: properties.description,
            links: properties.links,
          },
        };
      case "usecase":
        return {
          ...base,
          kind,
          properties: {
            description: properties.description,
            links: properties.links,
          },
        };
      case "entity":
        return {
          ...base,
          kind,
          properties: {
            description: properties.description,
            links: properties.links,
          },
          ...(properties.tableRef ? { tableRef: properties.tableRef } : {}),
        };
    }

    // Unreachable: resource and infra blocks are handled above
    this.error("invalid-node-kind", { kind });
    return {
      ...base,
      kind: "resource" as const,
      properties: { links: [] },
    };
  }

  /**
   * Parse `resource` declaration.
   * Supports two forms:
   *   1. Dot-notation reference: `resource OrderDB.C`
   *   2. Inline declaration:     `resource C { label "..." }`
   *      (inline without a database assignment emits an "unassigned-resource" warning)
   */
  private parseResourceDecl(start: Token): KrsNode {
    // id
    let id: string;
    let idToken: Token;
    if (this.peek().type !== TokenType.Identifier && this.peek().type !== TokenType.StringLiteral) {
      this.error("expected-node-id", { kind: "resource" });
      id = "__missing_id";
      idToken = start;
    } else {
      idToken = this.advance();
      id = idToken.value;
    }

    // Check for dot-notation: resource OrderDB.C
    let ref: { parent: string; child: string } | undefined;
    if (this.peek().type === TokenType.Dot) {
      const tail = readNodeIdPathTail(idToken, this.cursor, {
        maxSegments: 2,
        acceptStringSegments: true,
      });
      // On a dangling dot, keep this site's historical recovery: report, leave
      // the bad token unconsumed, and still join its value into the id.
      const childToken = tail.dangling ?? tail.end;
      if (tail.dangling) {
        this.error("expected-id-or-string", { context: "resource child id" });
      }
      ref = { parent: id, child: childToken.value };
      id = `${id}.${childToken.value}`;
      idToken = childToken;
    }

    const tags = this.parseTags();
    const { names: annotations, params: annotationParams } = this.parseAnnotations();
    const authorId = this.parseAuthorIdSuffix();

    const properties: CommonProperties & {
      label?: string;
      operations?: ParsedOperation[];
      facets?: string[];
    } = {
      links: [],
    };
    const children: KrsNode[] = [];
    const edges: KrsEdge[] = [];
    let end = idToken;

    if (this.peek().type === TokenType.LeftBrace) {
      this.advance();
      this.parseBlockContentsWithProperties(children, edges, "resource", properties);
      end = this.expect(TokenType.RightBrace);
    }

    // Inline resources (no dot-notation) that resolve to no store are surfaced
    // by the resolver's `detectUnassignedResources`, not here: a bare
    // `resource Order` may resolve to a unique `entity Order` declared anywhere
    // in the model, which the parser (single-declaration scope) cannot see.
    // Keeping the check model-wide is what makes the zero-edit promotion work
    // (the warning disappears once the matching entity is declared).

    let operations: ResourceOperation[] | undefined;
    if (properties.operations && properties.operations.length > 0) {
      operations = [];
      const seen = new Set<string>();
      for (const op of properties.operations) {
        const opLoc = op.decorationLoc
          ? this.range(op.decorationLoc, op.decorationLoc)
          : this.range(start.loc, end.loc);

        // De-dupe by verb (matching pre-decoration behavior).
        if (seen.has(op.verb)) {
          this.diagnostics.push({
            severity: "warning",
            code: "duplicate-resource-operation",
            params: { operation: op.verb, resourceId: id },
            loc: opLoc,
          });
          continue;
        }
        seen.add(op.verb);

        let decoratedAs: CrudVerb[] | undefined;
        if (op.decoration === undefined) {
          // Bare verb — current ADR-1046 behavior.
          if (!isRecognizedResourceOperation(op.verb)) {
            this.diagnostics.push({
              severity: "warning",
              code: "unknown-resource-operation",
              params: { operation: op.verb, resourceId: id },
              loc: opLoc,
            });
          }
        } else if (op.decoration.length === 0) {
          this.diagnostics.push({
            severity: "error",
            code: "empty-crud-decoration",
            params: { operation: op.verb, resourceId: id },
            loc: opLoc,
          });
          decoratedAs = [];
        } else {
          decoratedAs = [];
          const seenCrud = new Set<string>();
          for (const item of op.decoration) {
            if (!item.recognized) {
              this.diagnostics.push({
                severity: "error",
                code: "invalid-crud-decoration",
                params: { operation: op.verb, value: item.value, resourceId: id },
                loc: opLoc,
              });
              continue;
            }
            if (seenCrud.has(item.value)) {
              this.diagnostics.push({
                severity: "warning",
                code: "duplicate-crud-decoration-target",
                params: { operation: op.verb, value: item.value, resourceId: id },
                loc: opLoc,
              });
              continue;
            }
            seenCrud.add(item.value);
            decoratedAs.push(item.value as CrudVerb);
          }
        }

        operations.push({
          verb: op.verb,
          ...(decoratedAs !== undefined ? { decoratedAs } : {}),
        });
      }
    }

    return {
      kind: "resource",
      id,
      label: properties.label,
      tags,
      annotations,
      ...(Object.keys(annotationParams).length > 0 ? { annotationParams } : {}),
      children,
      edges,
      ...(properties.facets && properties.facets.length > 0 ? { facets: properties.facets } : {}),
      loc: this.range(start.loc, end.loc),
      properties: {
        description: properties.description,
        links: properties.links,
        ...(operations ? { operations } : {}),
      },
      ref,
      ...(authorId !== undefined ? { authorId } : {}),
    };
  }

  /**
   * Parse a top-level infra block: `database`, `queue`, or `storage`.
   * Each has a body of sub-resource declarations:
   *   database → table
   *   queue    → queue (parsed as queue-item internally)
   *   storage  → bucket
   */
  private parseInfraBlock(start: Token, kind: "database" | "queue" | "storage"): KrsNode {
    let id: string;
    let idToken: Token;
    if (this.peek().type !== TokenType.Identifier && this.peek().type !== TokenType.StringLiteral) {
      this.error("expected-node-id", { kind });
      id = "__missing_id";
      idToken = start;
    } else {
      idToken = this.advance();
      id = idToken.value;
    }

    const tags = this.parseTags();
    const { names: annotations, params: annotationParams } = this.parseAnnotations();
    const properties: CommonProperties & { label?: string; facets?: string[] } = { links: [] };
    const children: KrsNode[] = [];
    const edges: KrsEdge[] = [];
    const boundaries: BoundaryBlock[] = [];
    let end = idToken;

    if (this.peek().type === TokenType.LeftBrace) {
      this.advance();
      this.parseInfraBlockContents(children, edges, kind, properties, boundaries);
      end = this.expect(TokenType.RightBrace);
    }

    const base = {
      id,
      label: properties.label,
      tags,
      annotations,
      ...(Object.keys(annotationParams).length > 0 ? { annotationParams } : {}),
      children,
      edges,
      ...(boundaries.length > 0 ? { boundaries } : {}),
      ...(properties.facets && properties.facets.length > 0 ? { facets: properties.facets } : {}),
      loc: this.range(start.loc, end.loc),
      properties: {
        description: properties.description,
        links: properties.links,
      },
    };

    return { ...base, kind };
  }

  /** Parse the body of a database/queue/storage block. */
  private parseInfraBlockContents(
    children: KrsNode[],
    edges: KrsEdge[],
    parentKind: "database" | "queue" | "storage",
    properties: CommonProperties & { label?: string; facets?: string[] },
    boundaries: BoundaryBlock[],
  ): void {
    while (this.peek().type !== TokenType.RightBrace && this.peek().type !== TokenType.EOF) {
      const token = this.peek();

      if (token.type === TokenType.Label) {
        this.advance();
        if (this.peek().type === TokenType.StringLiteral) {
          properties.label = this.advance().value;
        } else {
          this.error("expected-string-after", { property: "label" });
        }
        continue;
      }

      if (token.type === TokenType.Description) {
        this.advance();
        properties.description = this.parseDescriptionValue();
        continue;
      }

      if (token.type === TokenType.Link) {
        this.advance();
        properties.links.push(this.parseLink());
        continue;
      }

      // Edge
      if (this.isEdgeStart(token)) {
        edges.push(this.parseEdge());
        continue;
      }

      // Sub-resource declarations
      const subNode = this.tryParseInfraSubNode(parentKind);
      if (subNode) {
        children.push(subNode);
        continue;
      }

      if (token.type === TokenType.Legend) {
        this.handleNestedLegend(parentKind);
        continue;
      }

      // Infra blocks draw a drill-down canvas of their leaf children, so they
      // host boundaries like any other container kind (#2036).
      if (token.type === TokenType.Boundary) {
        boundaries.push(this.parseBoundaryBlock());
        continue;
      }

      if (token.type === TokenType.Facets) {
        this.advance();
        properties.facets ??= [];
        this.parseFacetsList(properties.facets);
        continue;
      }

      if (token.type === TokenType.Facet) {
        this.rejectNestedFacetBlock(parentKind);
        continue;
      }

      this.error("unexpected-token-in-block", {
        blockKind: parentKind,
        tokenType: String(token.type),
        value: token.value,
      });
      this.advance();
    }
  }

  /**
   * Parse the body of a leaf sub-resource node (table, queue-item, bucket).
   * Only properties (label, description, link) and edges are allowed.
   * Child node declarations are intentionally excluded — sub-resources are leaf nodes
   * and must not contain nested infra blocks or logical node declarations.
   * Note: TokenType.Table and TokenType.Bucket are deliberately absent from
   * LOGICAL_KEYWORDS and isNodeKeywordType for the same reason.
   */
  private parseLeafNodeContents(
    edges: KrsEdge[],
    properties: CommonProperties & { label?: string; facets?: string[] },
    kind: "table" | "queue-item" | "bucket",
  ): void {
    while (this.peek().type !== TokenType.RightBrace && this.peek().type !== TokenType.EOF) {
      const token = this.peek();

      if (token.type === TokenType.Label) {
        this.advance();
        if (this.peek().type === TokenType.StringLiteral) {
          properties.label = this.advance().value;
        } else {
          this.error("expected-string-after", { property: "label" });
        }
        continue;
      }

      if (token.type === TokenType.Description) {
        this.advance();
        properties.description = this.parseDescriptionValue();
        continue;
      }

      if (token.type === TokenType.Link) {
        this.advance();
        properties.links.push(this.parseLink());
        continue;
      }

      if (this.isEdgeStart(token)) {
        edges.push(this.parseEdge());
        continue;
      }

      // A leaf holds no canvas, but it does hold data — a `table` is the
      // canonical carrier of a regulatory facet (CHD, PII), so `facets` is
      // accepted here exactly as on every other kind.
      if (token.type === TokenType.Facets) {
        this.advance();
        properties.facets ??= [];
        this.parseFacetsList(properties.facets);
        continue;
      }

      if (token.type === TokenType.Facet) {
        this.rejectNestedFacetBlock(kind);
        continue;
      }

      // A leaf draws no canvas, so a boundary here would have nothing to frame.
      // Named explicitly rather than falling through to unexpected-token, which
      // would not say why (#2036).
      if (token.type === TokenType.Boundary) {
        const block = this.parseBoundaryBlock();
        this.diagnostics.push({
          severity: "error",
          code: "boundary-not-in-context",
          params: { parentKind: kind },
          loc: block.loc,
        });
        continue;
      }

      this.error("unexpected-token-in-block", {
        blockKind: "sub-resource",
        tokenType: String(token.type),
        value: token.value,
      });
      this.advance();
    }
  }

  /** Try to parse a sub-resource node for the given parent infra kind. Returns null if not applicable. */
  private tryParseInfraSubNode(parentKind: "database" | "queue" | "storage"): KrsNode | null {
    const token = this.peek();

    if (parentKind === "database" && token.type === TokenType.Table) {
      return this.parseInfraSubNode("table");
    }
    if (parentKind === "queue" && token.type === TokenType.Queue) {
      return this.parseInfraSubNode("queue-item");
    }
    if (parentKind === "storage" && token.type === TokenType.Bucket) {
      return this.parseInfraSubNode("bucket");
    }

    return null;
  }

  /** Parse a sub-resource node (table, queue-item, bucket). */
  private parseInfraSubNode(kind: "table" | "queue-item" | "bucket"): KrsNode {
    const start = this.advance(); // keyword token (table / queue / bucket)

    let id: string;
    let idToken: Token;
    if (this.peek().type !== TokenType.Identifier && this.peek().type !== TokenType.StringLiteral) {
      this.error("expected-node-id", { kind: start.value });
      id = "__missing_id";
      idToken = start;
    } else {
      idToken = this.advance();
      id = idToken.value;
    }

    const tags = this.parseTags();
    const { names: annotations, params: annotationParams } = this.parseAnnotations();
    const properties: CommonProperties & { label?: string; facets?: string[] } = { links: [] };
    const children: KrsNode[] = [];
    const edges: KrsEdge[] = [];
    let end = idToken;

    if (this.peek().type === TokenType.LeftBrace) {
      this.advance();
      // Sub-resource nodes (table, queue-item, bucket) are leaf nodes: only properties and
      // edges are allowed inside their bodies. Using a restricted parser avoids accidentally
      // accepting infra keywords (database/queue/storage) as child nodes.
      this.parseLeafNodeContents(edges, properties, kind);
      end = this.expect(TokenType.RightBrace);
    }

    const base = {
      id,
      label: properties.label,
      tags,
      annotations,
      ...(Object.keys(annotationParams).length > 0 ? { annotationParams } : {}),
      children,
      edges,
      ...(properties.facets && properties.facets.length > 0 ? { facets: properties.facets } : {}),
      loc: this.range(start.loc, end.loc),
      properties: {
        description: properties.description,
        links: properties.links,
      },
    };

    return { ...base, kind } as TableNode | QueueItemNode | BucketNode;
  }

  private parseTags(): string[] {
    const tags: string[] = [];
    if (this.peek().type !== TokenType.LeftBracket) return tags;

    this.advance(); // [
    while (this.peek().type !== TokenType.RightBracket && this.peek().type !== TokenType.EOF) {
      if (this.peek().type === TokenType.Comma) {
        this.advance();
        continue;
      }
      // A tag value: an identifier, or a keyword token doubling as a tag name
      // (e.g. `[table]`). A kebab-case tag arrives as a `<word> - <word>` run
      // (the lexer emits `-` as its own token) — stitch it back into one tag
      // (#2509) so `[my-team-internal-tag]` is one tag, not seven.
      const first = this.advance();
      tags.push(stitchKebabTail(first, this.cursor).name);
    }
    this.expect(TokenType.RightBracket);
    return tags;
  }

  private parseAnnotations(): {
    names: string[];
    params: Record<string, Record<string, string>>;
  } {
    const names: string[] = [];
    const params: Record<string, Record<string, string>> = {};
    while (this.peek().type === TokenType.At) {
      this.advance(); // @
      if (this.peek().type !== TokenType.Identifier) continue;
      // Kebab-case annotation names (`@my-team-mark`) arrive as a
      // `<word> - <word>` token run — stitch, same as tags (#2509).
      const { name } = stitchKebabTail(this.advance(), this.cursor);
      names.push(name);
      // Optional parameters: `@name(key: "value"[, key: "value"]*)`.
      if (this.peek().type === TokenType.LeftParen) {
        this.advance(); // (
        while (this.peek().type !== TokenType.RightParen && this.peek().type !== TokenType.EOF) {
          // Skip separators between pairs.
          if (this.peek().type === TokenType.Comma || this.peek().type === TokenType.Colon) {
            this.advance();
            continue;
          }
          // Key name: an identifier, or a keyword that doubles as a key
          // (e.g. `from`, which the lexer tokenizes as a keyword). Read by
          // value so keyword-keys aren't mistaken for missing.
          const keyToken = this.advance();
          const key = keyToken.value;
          if (this.peek().type === TokenType.Colon) this.advance();
          // Value may be a quoted string (`until: "2026-Q3"`) or a bare
          // identifier referencing a node (`from: LegacyMonolith`).
          const valueType = this.peek().type;
          const value =
            valueType === TokenType.StringLiteral || valueType === TokenType.Identifier
              ? this.advance().value
              : "";
          const allowed = ANNOTATION_PARAM_KEYS[name];
          if (allowed?.has(key)) {
            (params[name] ??= {})[key] = value;
          } else {
            // Accepted-vocabulary rule (TPL-1503): a param with no
            // effect is warned, not silently kept. Only builtin keys have an
            // effect; custom annotations stay param-less for now (#1568).
            this.diagnostics.push({
              severity: "warning",
              code: "annotation-param-unsupported",
              params: { annotation: name, key },
              loc: this.range(keyToken.loc),
            });
          }
          if (this.peek().type === TokenType.Comma) this.advance();
        }
        if (this.peek().type === TokenType.RightParen) this.advance();
      }
    }
    return { names, params };
  }

  private parseEdge(implicitFrom?: string): KrsEdge {
    let fromValue: string;
    let startLoc: SourceLocation;
    if (implicitFrom) {
      // Implicit source: arrow token is first, source ID comes from parent block
      fromValue = implicitFrom;
      startLoc = this.peek().loc;
    } else {
      const fromToken = this.advance(); // from identifier or string literal
      fromValue = fromToken.value;
      startLoc = fromToken.loc;
    }
    const arrowToken = this.advance(); // -> or -->
    const toToken = this.parseIdOrString("edge target");

    // Support fully qualified cross-system references: SystemId.ServiceId
    let toValue = toToken.value;
    let toEnd = toToken.loc;
    if (this.peek().type === TokenType.Dot) {
      const tail = readNodeIdPathTail(toToken, this.cursor, {
        maxSegments: 2,
        acceptStringSegments: true,
      });
      // On a dangling dot, keep this site's historical recovery: report, leave
      // the bad token unconsumed, and still join its value into the target.
      const serviceToken = tail.dangling ?? tail.end;
      if (tail.dangling) {
        this.error("expected-id-or-string", { context: "qualified edge target" });
      }
      toValue = `${toToken.value}.${serviceToken.value}`;
      toEnd = serviceToken.loc;
    }

    let label: string | undefined;
    if (this.peek().type === TokenType.StringLiteral) {
      label = this.advance().value;
    }

    const tags = this.parseTags();
    const authorId = this.parseAuthorIdSuffix();

    return {
      from: fromValue,
      to: toValue,
      label,
      kind: arrowToken.type === TokenType.DashedArrow ? "async" : "sync",
      tags,
      loc: this.range(startLoc, toEnd),
      ...(authorId !== undefined ? { authorId } : {}),
    };
  }

  /**
   * Optional `#<id>` author identifier for edges and resource rows. The lexer
   * already emits `#foo` as a single Identifier token, so we just check the
   * leading `#`. Returns the id without the leading `#`, or undefined if the
   * next token is something else.
   */
  private parseAuthorIdSuffix(): string | undefined {
    const next = this.peek();
    if (next.type !== TokenType.Identifier) return undefined;
    if (!next.value.startsWith("#")) return undefined;
    if (next.value.length === 1) return undefined;
    this.advance();
    return next.value.slice(1);
  }

  private parseDeployBlock(): DeployBlock {
    const start = this.advance(); // deploy
    // Accept identifier (new: deploy Production) or string literal (legacy: deploy "name")
    const idToken =
      this.peek().type === TokenType.Identifier
        ? this.advance()
        : this.expect(TokenType.StringLiteral);
    this.expect(TokenType.LeftBrace);

    const nodes: DeployNode[] = [];
    let label: string | undefined;

    while (this.peek().type !== TokenType.RightBrace && this.peek().type !== TokenType.EOF) {
      if (this.peek().value === "label") {
        this.advance();
        if (
          this.peek().type === TokenType.StringLiteral ||
          this.peek().type === TokenType.Identifier
        ) {
          label = this.advance().value;
        } else {
          this.error("expected-property-value", { propName: "label" });
        }
      } else if (DEPLOY_KEYWORDS.has(this.peek().value)) {
        nodes.push(this.parseDeployNode());
      } else if (this.peek().type === TokenType.Legend) {
        this.handleNestedLegend("deploy");
      } else {
        this.error("unexpected-token-in-block", {
          blockKind: "deploy",
          tokenType: String(this.peek().type),
          value: this.peek().value,
        });
        this.advance();
      }
    }

    const end = this.expect(TokenType.RightBrace);

    return {
      id: idToken.value,
      label,
      nodes,
      loc: this.range(start.loc, end.loc),
    };
  }

  private parseDeployNode(): DeployNode {
    const start = this.advance(); // deploy kind keyword
    const kind = start.value as DeployNodeKind;
    // Accept identifier (new: oci myApp) or string literal (legacy: oci "my-app")
    const idToken =
      this.peek().type === TokenType.Identifier
        ? this.advance()
        : this.expect(TokenType.StringLiteral);
    this.expect(TokenType.LeftBrace);

    const properties: DeployNodeProperties = {};
    let label: string | undefined;

    while (this.peek().type !== TokenType.RightBrace && this.peek().type !== TokenType.EOF) {
      if (this.peek().value === "label") {
        this.advance();
        if (
          this.peek().type === TokenType.StringLiteral ||
          this.peek().type === TokenType.Identifier
        ) {
          label = this.advance().value;
        } else {
          this.error("expected-property-value", { propName: "label" });
        }
      } else if (DEPLOY_PROPERTY_KEYWORDS.has(this.peek().value)) {
        const propToken = this.advance();
        const propName = propToken.value as keyof DeployNodeProperties;
        if (propName === "realizes") {
          this.parseRealizesList(propToken, properties);
        } else if (
          // Value can be string literal or identifier
          this.peek().type === TokenType.StringLiteral ||
          this.peek().type === TokenType.Identifier
        ) {
          (properties as Record<string, string>)[propName] = this.advance().value;
        } else {
          this.error("expected-property-value", { propName });
        }
      } else if (this.peek().type === TokenType.Legend) {
        this.handleNestedLegend(kind);
      } else {
        this.error("unexpected-token-in-block", {
          blockKind: "deploy node",
          tokenType: String(this.peek().type),
          value: this.peek().value,
        });
        this.advance();
      }
    }

    const end = this.expect(TokenType.RightBrace);

    return {
      kind,
      id: idToken.value,
      label,
      properties,
      loc: this.range(start.loc, end.loc),
    };
  }

  /**
   * Parses the value of one `realizes` line, appending to the node's target
   * list. Targets may be written one per line (#409) or comma-separated on a
   * single line (#2167) — the comma form is sugar, so both append to the same
   * array and a node mixing the two accumulates in document order.
   *
   * **A list lives on the line its `realizes` keyword is on.** Both the commas
   * and the targets after them are held to that line, so the two malformed
   * spellings behave alike: neither `realizes A,` nor a following `,B` line
   * quietly extends the list across the line break. Property keywords carry
   * their own token types, so they could never be read as targets; what the
   * line rule actually stops is a bare identifier on the next line being
   * absorbed as a continuation instead of being reported where it sits.
   */
  private parseRealizesList(keyword: Token, properties: DeployNodeProperties): void {
    const { line } = keyword.loc;
    const onListLine = (token: Token): boolean => token.loc.line === line;
    const atTarget = (): boolean => {
      const token = this.peek();
      return (
        (token.type === TokenType.Identifier || token.type === TokenType.StringLiteral) &&
        onListLine(token)
      );
    };

    // The separator the parser is standing after, for the diagnostic below.
    // Undefined while reading the first target, which `realizes` introduces.
    let afterComma: Token | undefined;

    for (;;) {
      if (atTarget()) {
        const token = this.advance();
        (properties.realizes ??= []).push({
          id: token.value,
          loc: this.range(token.loc, token.end),
        });
        // Without a comma the list is done; with one, another target is due.
        if (this.peek().type !== TokenType.Comma || !onListLine(this.peek())) return;
        afterComma = this.advance();
        continue;
      }
      // No target where one is required: bare `realizes`, a leading comma
      // (`realizes ,B`), or the trailing comma of `realizes A,`. Report it once
      // rather than letting the comma fall through to the block loop's generic
      // `unexpected-token-in-block`, and anchor it on the token that is
      // actually wrong — the dangling comma, or `realizes` itself when the
      // value is missing outright. `this.error` would anchor on whatever comes
      // next, which for a trailing comma is the following (valid) line.
      this.diagnostics.push({
        severity: "error",
        code: "expected-property-value",
        params: { propName: "realizes" },
        loc: this.range((afterComma ?? keyword).loc, (afterComma ?? keyword).end),
      });
      // Swallow the stray separators so the same commas are not reported again,
      // then resume if that leaves a target to read — `realizes ,B` still
      // records `B`. Reaching here without a comma to consume means the line
      // holds nothing more to say, so stop rather than report twice.
      let swallowed = false;
      while (this.peek().type === TokenType.Comma && onListLine(this.peek())) {
        afterComma = this.advance();
        swallowed = true;
      }
      if (!swallowed || !atTarget()) return;
    }
  }

  // ─── Organization ──────────────────────────────────────────────────────────

  private parseOrganizationBlock(): OrganizationBlock {
    const start = this.advance(); // organization
    const idToken = this.parseIdOrString("organization");
    let label = this.parseRetiredPositionalLabel("organization");
    this.expect(TokenType.LeftBrace);

    const properties: CommonProperties = { links: [] };
    const teams: TeamNode[] = [];

    while (this.peek().type !== TokenType.RightBrace && this.peek().type !== TokenType.EOF) {
      const token = this.peek();
      if (token.type === TokenType.Label) {
        this.advance();
        if (this.peek().type === TokenType.StringLiteral) {
          label = this.advance().value;
        } else {
          this.error("expected-string-after", { property: "label" });
        }
      } else if (token.type === TokenType.Description) {
        this.advance();
        properties.description = this.parseDescriptionValue();
      } else if (token.type === TokenType.Link) {
        this.advance();
        properties.links.push(this.parseLink());
      } else if (token.type === TokenType.Team) {
        teams.push(this.parseTeamBlock());
      } else if (token.type === TokenType.Legend) {
        this.handleNestedLegend("organization");
      } else {
        this.error("unexpected-token-in-block", {
          blockKind: "organization",
          tokenType: String(token.type),
          value: token.value,
        });
        this.advance();
      }
    }

    const end = this.expect(TokenType.RightBrace);

    // Validate duplicate team IDs within this organization
    const seen = new Set<string>();
    this.collectTeamIds(teams, seen);

    return {
      id: idToken.value,
      label,
      properties,
      teams,
      loc: this.range(start.loc, end.loc),
    };
  }

  /**
   * The positional `<kw> <id> "<label>"` form on organization / team / member.
   * ADR-19 made `label` a property; these constructs kept the positional read as
   * undocumented leniency, deprecated in #2133 and removed in #2208.
   *
   * Unlike boundary / facet below, the value is **kept**. That is a fidelity
   * choice, not a rescue: recovery reads the string the author wrote rather
   * than discarding it, so `Parser.parse` callers that inspect the AST past the
   * diagnostic still see the name. It does not keep any drawing alive — every
   * render path refuses to draw while an error stands (the app republishes the
   * last valid SVG, `karasu render` and `karasu subtree` exit 1), and
   * `format()` refuses the source too, so `karasu fmt` can no longer rewrite
   * the form once it errors.
   */
  private parseRetiredPositionalLabel(construct: string): string | undefined {
    if (this.peek().type !== TokenType.StringLiteral) return undefined;
    this.error("positional-label-removed", { construct });
    return this.advance().value;
  }

  // Grammar: `boundary <id> { (label | description | link | contains)* }`
  // Mirrors parseOrganizationBlock; `contains <id>` reuses the one-target-per-line
  // shape of `owns` (parseTeamBlock). See docs/design/system-view-grouping.md
  // 「P2b 詳細設計」.
  private parseBoundaryBlock(): BoundaryBlock {
    const start = this.advance(); // boundary
    const idToken = this.parseIdOrString("boundary");
    let label: string | undefined;
    // The positional label form is removed on boundary (ADR-19 / #2133;
    // experimental construct, so no deprecation window). Consume the stray
    // string so parsing resumes cleanly at `{`.
    if (this.peek().type === TokenType.StringLiteral) {
      this.error("positional-label-removed", { construct: "boundary" });
      this.advance();
    }
    this.expect(TokenType.LeftBrace);

    const properties: CommonProperties = { links: [] };
    const contains: NodeIdPath[] = [];

    while (this.peek().type !== TokenType.RightBrace && this.peek().type !== TokenType.EOF) {
      const token = this.peek();
      if (token.type === TokenType.Label) {
        this.advance();
        if (this.peek().type === TokenType.StringLiteral) {
          label = this.advance().value;
        } else {
          this.error("expected-string-after", { property: "label" });
        }
      } else if (token.type === TokenType.Description) {
        this.advance();
        properties.description = this.parseDescriptionValue();
      } else if (token.type === TokenType.Link) {
        this.advance();
        properties.links.push(this.parseLink());
      } else if (token.type === TokenType.Contains) {
        this.advance();
        if (
          this.peek().type === TokenType.Identifier ||
          this.peek().type === TokenType.StringLiteral
        ) {
          const tail = readNodeIdPathTail(this.advance(), this.cursor, {
            acceptStringSegments: true,
          });
          if (tail.dangling) {
            // Dangling dot: report once and record nothing (#2088), matching
            // the `owns` reader in parseTeamBlock.
            this.error("expected-id-after", { property: "contains" });
          } else {
            contains.push(tail.segments);
          }
        } else {
          this.error("expected-id-after", { property: "contains" });
        }
      } else {
        this.error("unexpected-token-in-block", {
          blockKind: "boundary",
          tokenType: String(token.type),
          value: token.value,
        });
        this.advance();
      }
    }

    const end = this.expect(TokenType.RightBrace);

    return {
      kind: "boundary" as const,
      id: idToken.value,
      label,
      properties,
      contains,
      loc: this.range(start.loc, end.loc),
    };
  }

  // Grammar: `facet <id> { (label | description | link)* }` (#2065 Part B).
  //
  // Deliberately **not** `parseBoundaryBlock` with one more property: a facet
  // declaration has no membership list and no value language at all. `contains`
  // is rejected here like any other unexpected token, and that rejection is the
  // structural half of ADR-832's fence — the metadata home stays prose + link,
  // so there is no gradient from "declare a scope" to "declare a rule".
  private parseFacetBlock(): FacetBlock {
    const start = this.advance(); // facet
    const idToken = this.parseIdOrString("facet");
    let label: string | undefined;
    // Positional label is not part of the grammar (ADR-19 / #2133). `facet` is
    // new, so there is nothing to deprecate — reject and consume, as `boundary`
    // does, so parsing resumes cleanly at `{`.
    if (this.peek().type === TokenType.StringLiteral) {
      this.error("positional-label-removed", { construct: "facet" });
      this.advance();
    }
    this.expect(TokenType.LeftBrace);

    const properties: CommonProperties = { links: [] };

    while (this.peek().type !== TokenType.RightBrace && this.peek().type !== TokenType.EOF) {
      const token = this.peek();
      if (token.type === TokenType.Label) {
        this.advance();
        if (this.peek().type === TokenType.StringLiteral) {
          label = this.advance().value;
        } else {
          this.error("expected-string-after", { property: "label" });
        }
      } else if (token.type === TokenType.Description) {
        this.advance();
        properties.description = this.parseDescriptionValue();
      } else if (token.type === TokenType.Link) {
        this.advance();
        properties.links.push(this.parseLink());
      } else {
        this.error("unexpected-token-in-block", {
          blockKind: "facet",
          tokenType: String(token.type),
          value: token.value,
        });
        // Skip the whole offending clause, not just its first token. The
        // closed grammar makes this safe and worthwhile: the rejected forms are
        // the value-carrying ones (`contains Order`, `requires plan in [...]`),
        // and reporting each of their tokens separately would turn one authoring
        // mistake into a pile of diagnostics. Always advances at least once, so
        // the loop cannot stall.
        do {
          this.advance();
        } while (!this.isFacetPropertyStart(this.peek().type));
      }
    }

    const end = this.expect(TokenType.RightBrace);

    return {
      kind: "facet" as const,
      id: idToken.value,
      label,
      properties,
      loc: this.range(start.loc, end.loc),
    };
  }

  /** Tokens that begin a legal `facet` body entry, or end the block. */
  private isFacetPropertyStart(type: TokenType): boolean {
    return (
      type === TokenType.Label ||
      type === TokenType.Description ||
      type === TokenType.Link ||
      type === TokenType.RightBrace ||
      type === TokenType.EOF
    );
  }

  /**
   * Parse the value of a `facets` property: `<id>[, <id>]*`.
   *
   * Mirrors `parseHandlesList`. Ids are appended to `existing` rather than
   * returned fresh so repeated `facets` lines accumulate, and a duplicate id
   * (within one line or across lines) collapses — membership is a set, and
   * saying it twice is not a mistake worth a diagnostic.
   */
  private parseFacetsList(existing: string[]): void {
    const isId = (t: TokenType) => t === TokenType.Identifier || t === TokenType.StringLiteral;
    if (!isId(this.peek().type)) {
      this.error("expected-id-after", { property: "facets" });
      return;
    }
    const push = (id: string): void => {
      if (!existing.includes(id)) existing.push(id);
    };
    push(this.advance().value);
    while (this.peek().type === TokenType.Comma) {
      this.advance();
      if (!isId(this.peek().type)) {
        this.error("expected-id-after", { property: "facets" });
        return;
      }
      push(this.advance().value);
    }
  }

  /**
   * A `facet` block written inside a node block. Declarations are top-level only
   * — a facet is a model-wide name, not a per-node one — so consume the whole
   * block and report it once, instead of letting its body cascade into a dozen
   * unexpected-token errors.
   */
  private rejectNestedFacetBlock(blockKind: string): void {
    const token = this.peek();
    // Diagnostics raised while consuming the misplaced block are dropped: the
    // author's mistake is the placement, and complaints about the body of a
    // block that has no business being here are noise pointing at the wrong
    // fix. Truncating rather than not-parsing keeps the recovery — the block is
    // still consumed whole, so the enclosing block resumes cleanly.
    const before = this.diagnostics.length;
    const block = this.parseFacetBlock();
    this.diagnostics.length = before;
    this.diagnostics.push({
      severity: "error",
      code: "unexpected-token-in-block",
      params: { blockKind, tokenType: String(token.type), value: token.value },
      loc: block.loc,
    });
  }

  private parseTeamBlock(): TeamNode {
    const start = this.advance(); // team
    const idToken = this.parseIdOrString("team");
    let label = this.parseRetiredPositionalLabel("team");
    const { names: annotations, params: annotationParams } = this.parseAnnotations();
    this.expect(TokenType.LeftBrace);

    const properties: CommonProperties & { owns: NodeIdPath[] } = { links: [], owns: [] };
    const children: OrgNode[] = [];

    while (this.peek().type !== TokenType.RightBrace && this.peek().type !== TokenType.EOF) {
      const token = this.peek();
      if (token.type === TokenType.Label) {
        this.advance();
        if (this.peek().type === TokenType.StringLiteral) {
          label = this.advance().value;
        } else {
          this.error("expected-string-after", { property: "label" });
        }
      } else if (token.type === TokenType.Description) {
        this.advance();
        properties.description = this.parseDescriptionValue();
      } else if (token.type === TokenType.Link) {
        this.advance();
        properties.links.push(this.parseLink());
      } else if (token.type === TokenType.Owns) {
        this.advance();
        if (
          this.peek().type === TokenType.Identifier ||
          this.peek().type === TokenType.StringLiteral
        ) {
          const tail = readNodeIdPathTail(this.advance(), this.cursor, {
            acceptStringSegments: true,
          });
          if (tail.dangling) {
            // Dangling dot: report once and record nothing — recording the
            // read segments would put a wrong model next to the error (#2088).
            this.error("expected-id-after", { property: "owns" });
          } else {
            properties.owns.push(tail.segments);
          }
        } else {
          this.error("expected-id-after", { property: "owns" });
        }
      } else if (token.type === TokenType.Member) {
        children.push(this.parseMemberBlock());
      } else if (token.type === TokenType.Team) {
        children.push(this.parseTeamBlock());
      } else if (token.type === TokenType.Legend) {
        this.handleNestedLegend("team");
      } else {
        this.error("unexpected-token-in-block", {
          blockKind: "team",
          tokenType: String(token.type),
          value: token.value,
        });
        this.advance();
      }
    }

    const end = this.expect(TokenType.RightBrace);

    return {
      kind: "team" as const,
      id: idToken.value,
      label,
      annotations,
      ...(Object.keys(annotationParams).length > 0 ? { annotationParams } : {}),
      properties,
      children,
      loc: this.range(start.loc, end.loc),
    };
  }

  private parseMemberBlock(): MemberNode {
    const start = this.advance(); // member
    const idToken = this.parseIdOrString("member");
    let label = this.parseRetiredPositionalLabel("member");
    this.expect(TokenType.LeftBrace);

    const properties: CommonProperties & { slack?: string; github?: string } = { links: [] };

    while (this.peek().type !== TokenType.RightBrace && this.peek().type !== TokenType.EOF) {
      const token = this.peek();
      if (token.type === TokenType.Label) {
        this.advance();
        if (this.peek().type === TokenType.StringLiteral) {
          label = this.advance().value;
        } else {
          this.error("expected-string-after", { property: "label" });
        }
      } else if (token.type === TokenType.Description) {
        this.advance();
        properties.description = this.parseDescriptionValue();
      } else if (token.type === TokenType.Link) {
        this.advance();
        properties.links.push(this.parseLink());
      } else if (token.type === TokenType.Slack) {
        this.advance();
        if (this.peek().type === TokenType.StringLiteral) {
          properties.slack = this.advance().value;
        } else {
          this.error("expected-string-after", { property: "slack" });
        }
      } else if (token.type === TokenType.Github) {
        this.advance();
        if (this.peek().type === TokenType.StringLiteral) {
          properties.github = this.advance().value;
        } else {
          this.error("expected-string-after", { property: "github" });
        }
      } else if (token.type === TokenType.Legend) {
        this.handleNestedLegend("member");
      } else {
        this.error("unexpected-token-in-block", {
          blockKind: "member",
          tokenType: String(token.type),
          value: token.value,
        });
        this.advance();
      }
    }

    const end = this.expect(TokenType.RightBrace);

    return {
      kind: "member" as const,
      id: idToken.value,
      label,
      properties,
      children: [] as const,
      loc: this.range(start.loc, end.loc),
    };
  }

  private collectTeamIds(teams: TeamNode[], seen: Set<string>): void {
    for (const team of teams) {
      if (seen.has(team.id)) {
        this.diagnostics.push({
          severity: "error",
          code: "duplicate-team-id",
          params: { teamId: team.id },
          loc: team.loc,
        });
      } else {
        seen.add(team.id);
      }
      this.collectTeamIds(
        team.children.filter((c): c is TeamNode => c.kind === "team"),
        seen,
      );
    }
  }

  private buildNodePathIndex(
    systems: SystemNode[],
    domains: DomainNode[] = [],
    topLevelInfra: KrsNode[] = [],
  ): Map<string, string[]> {
    const index = new Map<string, string[]>();
    // INDEXED_KINDS governs the recursive walk() pass: service, domain, and
    // client are tracked here because they require migration-annotation priority
    // logic and cross-system duplicate detection, and because their paths are
    // what `viewPath` / permalinks address. resource / usecase / user are
    // intentionally excluded so a resource shared across usecases does not
    // register two locations for one id.
    //
    // `owns` is NOT among the consumers, though it was until #2082: editing this
    // set no longer changes which `owns` targets resolve. That question is
    // answered by `collectOwnsResolvableIds` in parser/reference-validation.ts,
    // against the merged tree, and since #2442 it does not consult kinds at all —
    // any declared node resolves, and refusing a kind is `invalid-owns`' sentence.
    // An index built per file could only answer it for whichever ids a given
    // merge path carried.
    //
    // Top-level infra nodes (database/queue/storage) and top-level clients are
    // indexed separately via the topLevelInfra loop below, which does not apply
    // these filters.
    const INDEXED_KINDS = new Set(["service", "domain", "client"]);
    // seenDomainIds is reset per system so that the same domain ID in different
    // systems does not trigger an error (cross-system parallel modelling is allowed).
    // The map value is the index priority of the already-stored domain:
    //   2 = @migration_target (active destination, highest priority)
    //   1 = no migration annotation
    //   0 = @deprecated (migration source, lowest priority)
    // A duplicate is allowed when at least one side carries a migration annotation.
    // The higher-priority domain wins the nodePathIndex entry.
    //
    // A domain with no annotations of its own inherits its parent service's
    // annotations for the priority computation (consistent with the rendering
    // inheritance — see docs/design/inherit-service-annotations.md). This
    // makes `service Legacy @deprecated { domain Order {} }` and
    // `service NewSvc @migration_target { domain Order {} }` a legal
    // migration-coexistence pair even without annotations on the domains.
    const walk = (
      node: KrsNode,
      path: string[],
      seenDomainIds: Map<string, number>,
      parentServiceAnnotations: string[],
    ): void => {
      const currentPath = [...path, node.id];
      if (INDEXED_KINDS.has(node.kind)) {
        if (node.kind === "domain") {
          const effective =
            node.annotations.length > 0 ? node.annotations : parentServiceAnnotations;
          const priority = migrationPriority(effective);
          if (seenDomainIds.has(node.id)) {
            const existingPriority = seenDomainIds.get(node.id)!;
            // A domain id shared by multiple services within one system is a
            // structural fact, not an error: karasu visualizes it, and the
            // resolver surfaces it through the `domain-dispersal` info
            // diagnostic (ADR-1386 — "smell is representable"). The
            // nodePathIndex keeps a single winner — the higher-priority
            // (migration-target) entry, or the first occurrence when
            // priorities tie — exactly as the migration-coexistence path
            // (ADR-477) already does.
            if (priority > existingPriority) {
              index.set(node.id, currentPath);
              seenDomainIds.set(node.id, priority);
            }
          } else {
            seenDomainIds.set(node.id, priority);
            index.set(node.id, currentPath);
          }
        } else {
          if (index.has(node.id)) {
            this.diagnostics.push({
              severity: "warning",
              code: "node-id-multiple-locations",
              params: { nodeId: node.id },
              loc: node.loc,
            });
          } else {
            index.set(node.id, currentPath);
          }
        }
      }
      const nextServiceAnnotations =
        node.kind === "service" ? node.annotations : parentServiceAnnotations;
      for (const child of node.children) {
        walk(child, currentPath, seenDomainIds, nextServiceAnnotations);
      }
    };
    for (const system of systems) {
      this.collectNodeIds(system.children, new Set<string>());
      const seenDomainIds = new Map<string, number>();
      for (const child of system.children) {
        walk(child, [system.id], seenDomainIds, []);
      }
    }
    // Index top-level domains (not nested in any system)
    // Each top-level domain is its own scope; no cross-domain uniqueness check needed here.
    for (const domain of domains) {
      // Duplicate-child check within the domain (e.g. a usecase and entity
      // sharing an id), symmetric with the per-service check inside systems.
      this.collectNodeIds(domain.children, new Set<string>());
      walk(domain, [], new Map<string, number>(), []);
    }
    // Index top-level infra nodes (database/queue/storage) and their sub-resources.
    for (const infra of topLevelInfra) {
      index.set(infra.id, [infra.id]);
      for (const child of infra.children) {
        index.set(child.id, [infra.id, child.id]);
      }
    }
    return index;
  }

  private collectNodeIds(nodes: KrsNode[], seen: Set<string>): void {
    for (const node of nodes) {
      if (seen.has(node.id)) {
        this.diagnostics.push({
          severity: "error",
          code: "duplicate-node-id-parent",
          params: { nodeId: node.id },
          loc: node.loc,
        });
      } else {
        seen.add(node.id);
      }
      this.collectNodeIds(node.children, new Set<string>());
    }
  }

  // ─── Legend block ────────────────────────────────────────────────────────
  // Grammar: `legend <view-scope>? <title?> { (swatch | ref)* }`
  //   view-scope ::= "system" | "deploy" | "org"
  //   swatch     ::= "swatch" "#" hex-id <string>
  //   ref        ::= "ref" ("@" id | "[" id "]" | "." id | "#" id | id) <string>
  //
  // Reference: docs/design/diagram-legend.md.

  /**
   * `legend` is only valid at the top level of a file (docs/spec/syntax.md
   * § Legend). When it appears inside a block, emit a single dedicated
   * diagnostic and skip the whole legend block so its contents do not
   * cascade into per-token `unexpected-token-in-block` errors.
   */
  private handleNestedLegend(parentKind: string): void {
    this.error("legend-not-top-level", { parentKind });
    this.advance(); // legend

    // Skip the optional view scope / title up to the legend's opening brace.
    // Stop early at `}` / EOF so a malformed legend (no body) does not eat
    // the enclosing block's closing brace.
    while (
      this.peek().type !== TokenType.LeftBrace &&
      this.peek().type !== TokenType.RightBrace &&
      this.peek().type !== TokenType.EOF
    ) {
      this.advance();
    }
    if (this.peek().type !== TokenType.LeftBrace) return;

    let depth = 0;
    while (this.peek().type !== TokenType.EOF) {
      const t = this.advance();
      if (t.type === TokenType.LeftBrace) depth++;
      else if (t.type === TokenType.RightBrace && --depth === 0) break;
    }
  }

  private parseLegendBlock(): LegendBlock {
    const start = this.advance(); // legend

    const scope = this.parseLegendScope();

    let title: string | undefined;
    if (this.peek().type === TokenType.StringLiteral) {
      title = this.advance().value;
    }

    this.expect(TokenType.LeftBrace);

    const entries: LegendEntry[] = [];
    while (this.peek().type !== TokenType.RightBrace && this.peek().type !== TokenType.EOF) {
      const token = this.peek();
      if (token.type === TokenType.Swatch) {
        const entry = this.parseLegendSwatch();
        if (entry) entries.push(entry);
      } else if (token.type === TokenType.Ref) {
        const entry = this.parseLegendRef();
        if (entry) entries.push(entry);
      } else {
        this.error("unexpected-token-in-block", {
          blockKind: "legend",
          tokenType: String(token.type),
          value: token.value,
        });
        this.advance();
      }
    }

    const end = this.expect(TokenType.RightBrace);

    return {
      scope,
      title,
      entries,
      loc: this.range(start.loc, end.loc),
    };
  }

  private parseLegendScope(): LegendViewScope | undefined {
    const next = this.peek();
    if (next.type === TokenType.System) {
      this.advance();
      return "system";
    }
    if (next.type === TokenType.Service) {
      this.advance();
      return "service";
    }
    if (next.type === TokenType.Domain) {
      this.advance();
      return "domain";
    }
    if (next.type === TokenType.Deploy) {
      this.advance();
      return "deploy";
    }
    // "org" is intentionally not a reserved keyword (the block keyword is
    // "organization"). Match it contextually as an identifier here.
    if (next.type === TokenType.Identifier && next.value === "org") {
      this.advance();
      return "org";
    }
    return undefined;
  }

  private parseLegendSwatch(): LegendEntry | null {
    const start = this.advance(); // swatch
    // The lexer emits `#xxx` as a single Identifier token (see lexer's
    // readHashToken). The parser stores the value verbatim and lets the
    // resolver / renderer validate the hex shape.
    const colorTok = this.expect(TokenType.Identifier);
    const labelTok = this.expect(TokenType.StringLiteral);
    return {
      kind: "swatch",
      color: colorTok.value,
      label: labelTok.value,
      loc: this.range(start.loc, labelTok.loc),
    };
  }

  private parseLegendRef(): LegendEntry | null {
    const start = this.advance(); // ref
    const target = this.parseLegendRefTarget();
    if (!target) {
      // Skip until label or end of entry to recover.
      if (this.peek().type === TokenType.StringLiteral) this.advance();
      return null;
    }
    const labelTok = this.expect(TokenType.StringLiteral);
    return {
      kind: "ref",
      target,
      label: labelTok.value,
      loc: this.range(start.loc, labelTok.loc),
    };
  }

  private parseLegendRefTarget(): LegendRefTarget | null {
    const tok = this.peek();
    switch (tok.type) {
      case TokenType.At: {
        this.advance();
        const first = this.expect(TokenType.Identifier);
        // Legend refs name the same vocabulary the model writes — stitch
        // kebab-case runs so `[my-team]` / `@my-mark` stay referenceable
        // (#2509).
        const { name } = stitchKebabTail(first, this.cursor);
        return { kind: "annotation", name };
      }
      case TokenType.LeftBracket: {
        this.advance();
        const first = this.expect(TokenType.Identifier);
        const { name } = stitchKebabTail(first, this.cursor);
        this.expect(TokenType.RightBracket);
        return { kind: "tag", name };
      }
      case TokenType.Dot: {
        this.advance();
        const name = this.expect(TokenType.Identifier);
        return { kind: "selector", selector: `.${name.value}` };
      }
      case TokenType.Identifier: {
        // `#xxx` is lexed as a single Identifier with the leading `#`
        // (see lexer's readHashToken). All other identifiers are bare type
        // selectors (e.g. unknown custom kinds).
        const name = this.advance();
        return { kind: "selector", selector: name.value };
      }
      default:
        // Accept node-kind keywords as type selectors (e.g. `service`,
        // `domain`, `client`). The lexer reserves these as keywords for
        // block parsing; here they are valid `.krs.style` selector names.
        if (isTypeSelectorKeyword(tok.type)) {
          const name = this.advance();
          return { kind: "selector", selector: name.value };
        }
        this.error("expected-identifier", {
          got: String(tok.type),
          value: tok.value,
        });
        this.advance();
        return null;
    }
  }
}

const TYPE_SELECTOR_KEYWORDS = new Set<TokenType>([
  TokenType.System,
  TokenType.Service,
  TokenType.Domain,
  TokenType.Usecase,
  TokenType.Resource,
  TokenType.User,
  TokenType.Client,
  TokenType.Database,
  TokenType.Queue,
  TokenType.Storage,
  TokenType.Table,
  TokenType.Bucket,
  TokenType.Team,
  TokenType.Member,
  TokenType.Organization,
]);

function isTypeSelectorKeyword(type: TokenType): boolean {
  return TYPE_SELECTOR_KEYWORDS.has(type);
}
